import { getOrgArtifactsByArtifactIdVersionsByVersionId, getOrgInvestigationsByIdOptions, postOrgInvestigationsSearchOptions } from '@onefootprint/fetch-dashboard';
import { useRequestErrorToast } from '@onefootprint/hooks';
import type { Investigation as InvestigationType, OrgArtifact } from '@onefootprint/request-types/dashboard';
import { useConfirmationDialog, useToast } from '@onefootprint/ui';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { parseAsString, useQueryState } from 'nuqs';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { INVESTIGATION_ARTIFACT_PARAM } from '@/config/constants';
import useDocumentsQueryParams from '@/entity/hooks/use-documents-query-params';
import useEntity from '@/entity/hooks/use-entity';
import { EntityIdProvider } from '@/entity/hooks/use-entity-id';
import type { BlocksState } from '@/features/gen-ui';
import useMobileLayout from '@/hooks/use-mobile-layout';
import usePermissions from '@/hooks/use-permissions';
import getEntityName from '@/utils/get-entity-name';
import ArtifactPreview from './components/artifact-preview';
import ArtifactSidebar, { isFinalArtifact, MobileSidebarBackdrop } from './components/artifact-sidebar';
import BlocksPanel from './components/blocks-panel';
import { PermissionErrorState } from './components/blocks-panel-states';
import ContentHeader from './components/content-header/content-header';
import { Header } from './components/header';
import { type ArtifactSelectorOptionId, GENERATED_SUMMARY_OPTION_ID } from './components/header/types';
import InvestigationDocumentViewer from './components/investigation-document-viewer/investigation-document-viewer';
import SourcesDialog from './components/sources-dialog';
import { SplitDisplay } from './components/split-display';
import useArtifactContent, { artifactContentQueryKey } from './hooks/use-artifact-content';
import useArtifactVersions from './hooks/use-artifact-versions';
import useArtifacts from './hooks/use-artifacts';
import useCreateArtifact from './hooks/use-create-artifact';
import useDeleteArtifact from './hooks/use-delete-artifact';
import useInvestigationSidebar from './hooks/use-investigation-sidebar';
import useInvestigationTrace from './hooks/use-investigation-trace';
import useJudgmentValues from './hooks/use-judgment-values';
import useReadMode from './hooks/use-read-mode';
import useRenameArtifact from './hooks/use-rename-artifact';
import useResolveSelectedArtifact from './hooks/use-resolve-selected-artifact';
import useSaveArtifactVersion from './hooks/use-save-artifact-version';
import useSourcesDialog from './hooks/use-sources-dialog';
import { decodeBase64Utf8 } from './utils/decode-base64';
import deriveDraftName from './utils/derive-draft-name';
import encodeBase64 from './utils/encode-base64';
import { exportReportToPdf, sanitizePdfFilename } from './utils/export-pdf';
import isEmptyMarkdown from './utils/is-empty-markdown';
import reportCardsOf from './utils/report-cards-of';
const BLANK_DOCUMENT_INITIAL_CONTENT = '# ';
type Props = {
  investigation: InvestigationType;
  blocks: BlocksState;
};

// The chat lives in the global widget (investigation-scoped mode); this component
// renders only the left panel — the blocks/markdown/artifact editor.
const Investigation = ({
  investigation,
  blocks
}: Props) => {
  const queryClient = useQueryClient();
  // Investigations may have no entity (e.g. a Percy orchestrator parent). The
  // fetch is disabled when fpId is absent, so entity stays undefined and the
  // header degrades to its entity-less shape.
  const {
    data: entity
  } = useEntity(investigation.fpId ?? '');
  const {
    push: openDocument
  } = useDocumentsQueryParams();
  const {
    data: artifacts = []
  } = useArtifacts(investigation.investigationId);
  const subInvestigationsQuery = useQuery({
    ...postOrgInvestigationsSearchOptions({
      body: {
        parentInvestigationId: investigation.investigationId,
        pagination: {
          pageSize: 5
        }
      }
    }),
    enabled: !!investigation.investigationId
  });
  const subInvestigations = (subInvestigationsQuery.data?.data ?? []).filter(child => child.parentInvestigationId === investigation.investigationId && child.investigationId !== investigation.investigationId);
  // When this is a child, fetch its parent so the header dropdown can show the parent's name and
  // entity rather than a bare id.
  const parentInvestigationQuery = useQuery({
    ...getOrgInvestigationsByIdOptions({
      path: {
        id: investigation.parentInvestigationId ?? ''
      }
    }),
    enabled: !!investigation.parentInvestigationId
  });
  const deleteArtifact = useDeleteArtifact();
  const saveArtifactVersion = useSaveArtifactVersion();
  const createArtifact = useCreateArtifact();
  const renameArtifact = useRenameArtifact();
  const {
    t
  } = useTranslation('artifact', {
    keyPrefix: 'actions'
  });
  const {
    toast
  } = useToast();
  const confirm = useConfirmationDialog();
  // No default: an absent param means "not explicitly chosen", so we can fall back to the agent's
  // final-output artifact. An explicit `generated-summary` value still pins the summary.
  const [selectedArtifactOption, setSelectedArtifactOption] = useQueryState(INVESTIGATION_ARTIFACT_PARAM, parseAsString);
  const pdfTargetRef = useRef<HTMLDivElement>(null);
  const markdownContentRef = useRef<HTMLDivElement>(null);
  const showErrorToast = useRequestErrorToast();
  const [isExporting, setIsExporting] = useState(false);
  const {
    hasPermission
  } = usePermissions();
  const canDecryptArtifacts = hasPermission('decrypt_artifacts');

  // Group artifacts under the agent task that produced them, from the run's trace. Undefined for
  // non-agent investigations, which fall back to the sidebar's flat list.
  const {
    data: plan
  } = useInvestigationTrace({
    investigationId: investigation.investigationId,
    enabled: !!investigation.agent
  });

  // Each judgment's value for its sidebar tag.
  const judgmentValues = useJudgmentValues({
    artifacts,
    enabled: canDecryptArtifacts
  });

  // Markdown edit state — scoped to the currently-selected markdown artifact.
  // `baseline` is what was last persisted (start: the prop content; after Save: the saved version).
  // `current` is the live value in the editor.
  // Track the artifact id alongside so we can reset on switch.
  const [edit, setEdit] = useState<{
    artifactId: string;
    baseline: string;
    current: string;
  } | null>(null);
  // Bumped when edits are discarded to force the NotionEditor to remount.
  // NotionEditor's prevContent ref doesn't update while the editor is focused, so
  // when the prop reverts back to that same baseline after a discard, its sync
  // effect treats it as a no-op and the edits remain on screen.
  const [editorRemountKey, setEditorRemountKey] = useState(0);

  // When set, the inline editor auto-focuses its first node on next mount —
  // used when creating a blank document. Cleared by MarkdownView once focus has been applied.
  const [autoFocusDraftId, setAutoFocusDraftId] = useState<string | null>(null);

  // The single in-flight draft markdown artifact, if any. At most one at a time —
  // selecting another artifact (or creating a new draft) prompts a confirmation that discards the current draft.
  const [draftArtifact, setDraftArtifact] = useState<{
    artifact: OrgArtifact;
    contentBase64: string;
  } | null>(null);

  // When set, the user has clicked a past version in the history dialog and is previewing/editing it;
  // saving creates a new version with the (possibly further-edited) restored content.
  // Cleared on save success, discard, artifact switch, or delete.
  const [restoringFromVersionId, setRestoringFromVersionId] = useState<string | null>(null);
  const isRestoring = restoringFromVersionId !== null;
  const isMobile = useMobileLayout();
  const sidebar = useInvestigationSidebar();
  const allArtifacts = draftArtifact ? [...artifacts, draftArtifact.artifact] : artifacts;

  // With no explicit choice: the agent's final artifact, else the most-recent artifact, else the
  // generated summary — which only has the sidebar to itself when nothing else exists.
  const getDefaultArtifactOption = () => {
    const finalArtifactId = [...allArtifacts].sort((a, b) => b.createdAt.localeCompare(a.createdAt)).filter(isFinalArtifact)[0]?.id;
    if (finalArtifactId) return finalArtifactId;
    const mostRecentArtifactId = allArtifacts.filter(a_0 => a_0.metadata.kind !== 'ui_block').sort((a_1, b_0) => b_0.createdAt.localeCompare(a_1.createdAt))[0]?.id;
    return mostRecentArtifactId ?? GENERATED_SUMMARY_OPTION_ID;
  };
  const effectiveArtifactOption = selectedArtifactOption ?? getDefaultArtifactOption();
  useResolveSelectedArtifact({
    selectedOptionId: effectiveArtifactOption,
    artifacts: allArtifacts,
    investigationId: investigation.investigationId
  });
  const isDraftArtifact = (artifactId: string) => draftArtifact?.artifact.id === artifactId;
  const handleDeleteArtifact = (artifactId_0: string) => {
    const doDelete = () => {
      if (effectiveArtifactOption === artifactId_0) {
        setSelectedArtifactOption(null);
      }
      if (isDraftArtifact(artifactId_0)) {
        // Drafts never made it to the server, so there's nothing to delete remotely.
        setDraftArtifact(null);
        return;
      }
      deleteArtifact.mutate({
        path: {
          artifactId: artifactId_0
        }
      });
    };
    // Blank drafts have nothing to lose — skip the destructive confirmation.
    if (isBlankDraft && isDraftArtifact(artifactId_0)) {
      doDelete();
      return;
    }
    const artifactName = allArtifacts.find(a_2 => a_2.id === artifactId_0)?.name ?? '';
    confirm.open({
      title: t($ => $.deleteDialog.title),
      description: t($_0 => $_0.deleteDialog.description, {
        name: artifactName
      }),
      variant: 'destructive',
      primaryButton: {
        label: t($_1 => $_1.deleteDialog.confirm),
        onClick: doDelete
      },
      secondaryButton: {
        label: t($_2 => $_2.deleteDialog.cancel)
      }
    });
  };

  // Posts the current draft to the server, swaps the local draft id for the server-assigned id,
  // and pre-populates the list + content caches.
  const graduateDraft = (name: string) => {
    if (!edit || !draftArtifact || draftArtifact.artifact.id !== edit.artifactId) return;

    // Snapshot at submit time — see the version-save flow for why this matters.
    const savedContent = edit.current;
    createArtifact.mutate({
      body: {
        file: new Blob([savedContent], {
          type: 'text/markdown'
        }),
        // `workflow_id` ties the artifact to this investigation — without it, the artifact is
        // created but doesn't show up in the workflow-filtered list query on refresh.
        metadata: JSON.stringify({
          name,
          mime_type: 'text/markdown',
          investigation_id: investigation.investigationId
        })
      }
    }, {
      onSuccess: ({
        id: serverId
      }) => {
        // Pre-populate the content cache so the inline editor doesn't blank-flash while
        // `useArtifactContent` fetches the bytes we already have in memory.
        queryClient.setQueryData(artifactContentQueryKey(serverId), encodeBase64(savedContent));
        setDraftArtifact(null);
        setSelectedArtifactOption(serverId);
        setEdit(null);
        readMode.stopEditing();
        toast({
          variant: 'success',
          title: t($_3 => $_3.saveToast.title),
          description: t($_4 => $_4.saveToast.description)
        });
      }
    });
  };
  const handleRenameArtifact = (artifactId_1: string, newName: string) => {
    if (isDraftArtifact(artifactId_1)) {
      // Renaming a draft = creates an artifact with this name
      graduateDraft(newName);
      return;
    }
    // Rename is metadata-only and is allowed even while content edits are unsaved.
    renameArtifact.mutate({
      path: {
        artifactId: artifactId_1
      },
      body: {
        name: newName
      }
    }, {
      onSuccess: () => {
        toast({
          variant: 'success',
          title: t($_5 => $_5.renameToast.title),
          description: t($_6 => $_6.renameToast.description)
        });
      }
    });
  };

  // Fetches the past version's bytes and seeds the editor as a read-only preview.
  // Baseline is the current latest content, clicking "Restore this version" commits the
  // unchanged restored bytes as a new version. If the user wants to tweak further, they save then edit normally.
  // The "Current" entry in the dialog is non-clickable, so a no-op guard isn't needed here.
  const handleSelectVersion = async (versionId: string) => {
    if (!selectedArtifact) return;
    const artifactId_2 = selectedArtifact.id;
    const latestText = selectedArtifactContent ? decodeBase64Utf8(selectedArtifactContent) ?? '' : '';
    try {
      const response = await getOrgArtifactsByArtifactIdVersionsByVersionId({
        path: {
          artifactId: artifactId_2,
          versionId
        },
        parseAs: 'blob',
        throwOnError: true
      });
      // Restore is only offered for markdown artifacts, which round-trip as UTF-8 text.
      const restoredText = await (response.data as Blob).text();
      setEdit({
        artifactId: artifactId_2,
        baseline: latestText,
        current: restoredText
      });
      setRestoringFromVersionId(versionId);
      // Force the editor to remount so the prop change takes effect even if it was focused.
      setEditorRemountKey(k => k + 1);
    } catch (err) {
      showErrorToast(err);
    }
  };
  const handleBlankDocument = () => {
    sidebar.closeOnMobile();
    const id = `art_draft_${crypto.randomUUID()}`;
    readMode.startEditing(id);
    const newDraft: OrgArtifact = {
      id,
      name: 'Untitled.md',
      mimeType: 'text/markdown',
      investigationId: investigation.investigationId,
      kind: 'file',
      metadata: {
        kind: 'file',
        data: {
          originalFilename: 'Untitled.md'
        }
      },
      createdAt: new Date().toISOString(),
      createdByActor: {
        kind: 'footprint'
      }
    };
    setDraftArtifact({
      artifact: newDraft,
      contentBase64: encodeBase64(BLANK_DOCUMENT_INITIAL_CONTENT)
    });
    setSelectedArtifactOption(id);
    // Seed edit state as dirty (baseline = empty) so Save/Discard appear immediately.
    setEdit({
      artifactId: id,
      baseline: '',
      current: BLANK_DOCUMENT_INITIAL_CONTENT
    });
    // Place the editor's cursor inside the seeded H1 placeholder once it mounts.
    setAutoFocusDraftId(id);
  };
  const selectedArtifact = effectiveArtifactOption === GENERATED_SUMMARY_OPTION_ID ? null : allArtifacts.find(a_3 => a_3.id === effectiveArtifactOption) ?? null;

  // Discard pending edits if the user switched artifacts so stale state doesn't leak into the freshly-selected one.
  const activeEdit = edit && edit.artifactId === selectedArtifact?.id ? edit : null;
  if (edit && !activeEdit) {
    // In-render reset: safe per React's "store information from previous renders" pattern, since the comparison won't loop.
    setEdit(null);
    setRestoringFromVersionId(null);
  }
  const isMarkdownDirty = !!activeEdit && activeEdit.current !== activeEdit.baseline;

  // A draft with no content beyond the seeded H1 placeholder is considered blank
  const isBlankDraft = !!activeEdit && !!draftArtifact && draftArtifact.artifact.id === activeEdit.artifactId && isEmptyMarkdown(activeEdit.current);
  const isSaveDisabled = isBlankDraft;
  const isRenameDisabled = isBlankDraft;
  const isSaving = createArtifact.isPending || saveArtifactVersion.isPending || renameArtifact.isPending;
  const handleEditorChange = (md: string) => {
    if (!selectedArtifact) return;
    setEdit(prev => {
      const baseline = prev && prev.artifactId === selectedArtifact.id ? prev.baseline : md;
      return {
        artifactId: selectedArtifact.id,
        baseline,
        current: md
      };
    });
  };
  const handleSaveEdits = () => {
    if (!edit) return;
    if (isDraftArtifact(edit.artifactId)) {
      graduateDraft(deriveDraftName(edit.current));
      return;
    }

    // Save is only visible when the editor is dirty, and dirty is gated on
    // `selectedArtifact?.id === edit.artifactId`, so this is always defined.
    if (!selectedArtifact) return;

    // Snapshot at submit time — the user may keep typing while the request is in
    // flight, and `baseline` should reflect only what was sent.
    const savedContent_0 = edit.current;
    const savedArtifactId = edit.artifactId;
    const metadata = JSON.stringify({
      name: selectedArtifact.name,
      mime_type: selectedArtifact.mimeType
    });
    saveArtifactVersion.mutate({
      path: {
        artifactId: savedArtifactId
      },
      body: {
        file: new Blob([savedContent_0], {
          type: 'text/markdown'
        }),
        metadata
      }
    }, {
      onSuccess: () => {
        setEdit(prev_0 => prev_0 && prev_0.artifactId === savedArtifactId ? {
          ...prev_0,
          baseline: savedContent_0
        } : prev_0);
        // Exit restore mode (no-op when not restoring) so the UI returns to the normal layout.
        setRestoringFromVersionId(null);
        readMode.stopEditing();
        toast({
          variant: 'success',
          title: t($_7 => $_7.saveToast.title),
          description: t($_8 => $_8.saveToast.description)
        });
      }
    });
  };
  const handleDiscardEdits = () => {
    // Effectively empty draft (just the seeded H1) — nothing to lose. Abandon
    // the draft entirely without a confirmation, mirroring `guardInlineAction`.
    if (isBlankDraft) {
      setEdit(null);
      setRestoringFromVersionId(null);
      setDraftArtifact(null);
      setSelectedArtifactOption(null);
      readMode.stopEditing();
      return;
    }
    confirm.open({
      title: t($_9 => $_9.discardConfirmDialog.title),
      description: t($_10 => $_10.discardConfirmDialog.description),
      variant: 'destructive',
      primaryButton: {
        label: t($_11 => $_11.discardConfirmDialog.confirm),
        onClick: () => {
          // Clear `edit` entirely (rather than reverting `current` to `baseline`) so the
          // editor remounts from the prop content. Otherwise restore's raw `baseline` can
          // disagree with the editor's serialized output and re-mark the doc as dirty.
          setEdit(null);
          setRestoringFromVersionId(null);
          setEditorRemountKey(k_0 => k_0 + 1);
          readMode.stopEditing();
        }
      },
      secondaryButton: {
        label: t($_12 => $_12.discardConfirmDialog.cancel)
      }
    });
  };
  const handleArtifactSelect = (optionId: ArtifactSelectorOptionId) => {
    sidebar.closeOnMobile();
    // Clicking the already-selected option is a no-op.
    if (optionId === effectiveArtifactOption) return;
    readMode.stopEditing();
    // The selector is hidden while a markdown artifact has unsaved edits (see the header's
    // ActionButtons), so a switch here can't silently drop dirty work — no confirmation needed.
    setSelectedArtifactOption(optionId);
  };
  // Show the left panel whenever there's content to render or no live session (replay/historical investigations).
  // Brand-new sessions with zero blocks and no artifacts collapse to the chat-only column.
  const layoutReady = !blocks.isLoading;
  const isExportDisabled = !blocks.blocks?.length;
  const handleExportPdf = async () => {
    if (isExportDisabled || isExporting) return;
    setIsExporting(true);
    // Yield two frames so React can flush the spinner before the heavy
    // synchronous work in toCanvas blocks the main thread.
    await new Promise<void>(resolve => requestAnimationFrame(() => requestAnimationFrame(() => resolve())));
    try {
      const el = pdfTargetRef.current;
      if (!el) return;
      const filename = sanitizePdfFilename(`${investigation.title || getEntityName(investigation)}-report`);
      await exportReportToPdf(el, filename);
    } catch (err_0) {
      showErrorToast(err_0);
    } finally {
      setIsExporting(false);
    }
  };

  // Native browser warning on refresh/tab-close when unsaved work would be lost.
  // Mirrors `guardInlineAction`: blank drafts skip the prompt since there's nothing to lose.
  useEffect(() => {
    const shouldWarn = (isMarkdownDirty || !!draftArtifact) && !isBlankDraft;
    if (!shouldWarn) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isMarkdownDirty, draftArtifact, isBlankDraft]);

  // Drafts live entirely client-side until first save, real artifacts fetch on demand.
  const isSelectedADraft = !!selectedArtifact && isDraftArtifact(selectedArtifact.id);
  const {
    data: selectedRemoteContent
  } = useArtifactContent(isSelectedADraft || !canDecryptArtifacts ? null : selectedArtifact?.id ?? null);
  const selectedArtifactContent = isSelectedADraft ? draftArtifact?.contentBase64 : selectedRemoteContent;
  // An agent's report reads as section cards, which can't be edited in place, so it opens read-only.
  // Judged on the saved bytes, not the live edit, or typing a `##` would swap the editor for cards.
  const readMode = useReadMode({
    isRestoring,
    documentId: selectedArtifact?.id ?? null,
    readFirst: !!selectedArtifactContent && reportCardsOf(selectedArtifact, decodeBase64Utf8(selectedArtifactContent)) !== null
  });
  const sourcesDialog = useSourcesDialog({
    investigationId: investigation.investigationId,
    selectedArtifact,
    selectedArtifactContent,
    editingMarkdownContent: activeEdit?.current
  });

  // Restore remains markdown-only, but version metadata should be visible for read-only artifacts too.
  const {
    data: selectedArtifactVersions = []
  } = useArtifactVersions(selectedArtifact && !isSelectedADraft ? selectedArtifact.id : null);

  // Without decrypt_artifacts the bytes endpoint 403s — surface the permission state instead of spinning forever.
  const isPermissionBlocked = !!selectedArtifact && !isSelectedADraft && !canDecryptArtifacts;

  // Committed to rendering a selected artifact inline, but its bytes haven't arrived yet.
  const isLoadingSelectedContent = !!selectedArtifact && !isSelectedADraft && selectedArtifactContent === undefined && !isPermissionBlocked;
  const renderInlineContent = () => {
    if (isPermissionBlocked) {
      return <PermissionErrorState description={t($_13 => $_13.permissionError.description)} />;
    }
    // No artifact selected — show the Generated Summary (gen-ui blocks).
    if (selectedArtifact === null) {
      return <BlocksPanel blocks={blocks} pdfTargetRef={pdfTargetRef} />;
    }
    return <ArtifactPreview artifact={selectedArtifact} entity={entity} contentBase64={selectedArtifactContent} isLoading={isLoadingSelectedContent} agent={investigation.agent} contentRef={markdownContentRef}
    // Markdown-only props (ignored by the read-only viewers).
    editable={readMode.isEditable} onChange={handleEditorChange} autoFocus={selectedArtifact.id === autoFocusDraftId} onAutoFocused={() => setAutoFocusDraftId(null)} editingMarkdownContent={activeEdit?.current} markdownEditorKey={`${selectedArtifact.id}-${editorRemountKey}`} onCitationPairsChange={sourcesDialog.onCitationPairsChange} onOpenSources={sourcesDialog.open} onOpenDocument={investigation.fpId ? openDocument : undefined} />;
  };
  const contentHeader = <ContentHeader artifact={selectedArtifact} artifactContent={selectedArtifactContent} versions={selectedArtifactVersions} onSelectVersion={handleSelectVersion} onRenameArtifact={handleRenameArtifact} onDeleteArtifact={handleDeleteArtifact} markdownContentRef={markdownContentRef} isMarkdownDirty={isMarkdownDirty} isRestoring={isRestoring} isSaveDisabled={isSaveDisabled} isRenameDisabled={isRenameDisabled} isSaving={isSaving} onSaveEdits={handleSaveEdits} onDiscardEdits={handleDiscardEdits} onExportPdf={handleExportPdf} isExporting={isExporting} isExportDisabled={isExportDisabled} onStartEditing={readMode.onStartEditing} onStopEditing={readMode.onStopEditing} onToggleSidebar={isMobile ? sidebar.toggle : undefined} isSidebarOpen={sidebar.isOpen} />;
  return <SplitDisplay isReady={layoutReady} showLeftContent showRightContent={false} rightContent={null} leftContent={<div className="w-full h-full flex flex-col">
          <EntityIdProvider value={investigation.fpId || undefined}>
            <Header investigation={investigation} entity={entity} subInvestigations={subInvestigations} hasMoreSubInvestigations={!!subInvestigationsQuery.data?.meta.next} subInvestigationsParentId={investigation.investigationId} parentInvestigation={parentInvestigationQuery.data} isSidebarOpen={sidebar.isOpen} onToggleSidebar={sidebar.toggle} sourceCount={sourcesDialog.sources.length} citedSourceCount={sourcesDialog.citedSourceCount} onOpenSources={sourcesDialog.open} />
          </EntityIdProvider>
          {/* On a phone the artifact list opens under the title that toggles it. */}
          {isMobile && contentHeader}
          <div className="relative flex-1 min-h-0 flex overflow-hidden">
            <MobileSidebarBackdrop open={sidebar.isOpen} onClose={sidebar.close} />
            <ArtifactSidebar artifacts={allArtifacts} plan={plan} judgmentValues={judgmentValues} outputs={investigation.agent?.finalOutput} selectedOptionId={effectiveArtifactOption} onSelect={handleArtifactSelect} onBlankDocument={handleBlankDocument} disabled={isMarkdownDirty || isRestoring} open={sidebar.isOpen} />
            {/* The editor's own z-indexed chrome (drag handles, chart tooltips) would otherwise paint
                over the mobile sidebar overlay. */}
            <div className="flex-1 min-h-0 relative overflow-hidden flex flex-col isolate md:isolation-auto">
              {!isMobile && contentHeader}
              <div className="flex-1 min-h-0 relative overflow-hidden flex flex-col">{renderInlineContent()}</div>
            </div>
          </div>
          <SourcesDialog open={sourcesDialog.isOpen} onClose={sourcesDialog.close} sources={sourcesDialog.sources} citedSourceLabels={sourcesDialog.citedSourceLabels} onOpenDocument={investigation.fpId ? openDocument : undefined} />
          <InvestigationDocumentViewer entity={entity} />
        </div>} />;
};
export default Investigation;